/**
 * React Query hooks for session data management
 *
 * Provides hooks for fetching session data with
 * automatic cache invalidation.
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { sessionService } from '../services/api';
import {
  Session,
  SessionListResponse,
  SessionFilters,
} from '../types/session';

const SESSIONS_QUERY_KEY = 'sessions';

/**
 * Hook to fetch a single session by ID
 */
export function useSession(id: string | undefined): UseQueryResult<Session, Error> {
  return useQuery({
    queryKey: [SESSIONS_QUERY_KEY, id],
    queryFn: () => sessionService.get(id!),
    enabled: !!id, // Only run query if id is provided
    staleTime: 30000, // Keep data fresh for 30 seconds
  });
}

/**
 * Hook to fetch list of sessions for a job
 */
export function useSessionsByJob(jobId: string | undefined, filters?: SessionFilters): UseQueryResult<SessionListResponse, Error> {
  return useQuery({
    queryKey: [SESSIONS_QUERY_KEY, 'job', jobId, filters],
    queryFn: () => sessionService.listByJob(jobId!, filters),
    enabled: !!jobId, // Only run query if jobId is provided
    staleTime: 30000,
  });
}

/**
 * Get the SSE stream URL for a session
 */
export function getSessionStreamUrl(sessionId: string): string {
  return sessionService.getStreamUrl(sessionId);
}
